import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../Services/api';
import Globals from '../../../Globals.json';
import { buildDashboardQuery, isRangeReady } from '../dateFilters';

/**
 * Fonte de dados dos painéis Vendas por Vendedor, Vendas por Horário, Métodos
 * de Pagamento e dos KPIs do topo.
 *
 * Até a entrega das rotas agregadas, isto aqui baixava `/api/v1/sales` página
 * a página e somava no navegador — com teto de 20 páginas, o que truncava
 * silenciosamente lojas de volume alto. Agora são quatro chamadas diretas a
 * `/api/v1/dashboard/`, que agregam no banco e aceitam intervalo livre.
 */

export interface SellerTotal {
  id: number | string;
  name: string;
  total: number;
  orders: number;
}

export interface HourTotal {
  hour: number;
  label: string;
  total: number;
  orders: number;
}

export interface PaymentTotal {
  id: number | string;
  name: string;
  total: number;
  orders: number;
}

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

interface SellerRow {
  seller_id: number | null;
  seller_name: string | null;
  total_value: number | string;
  total_orders: number | string;
}

interface HourRow {
  hour: number;
  total_value: number | string;
  total_orders: number | string;
}

interface PaymentRow {
  payment_type_id: number | null;
  payment_method: string | null;
  total_value: number | string;
  total_orders: number | string;
}

interface SummaryResponse {
  total_value: number | string;
  total_orders: number | string;
  average_ticket: number | string;
  customers_served: number | string;
  new_customers: number | string;
  /** Somatório da taxa de serviço cobrada no período. */
  service_tax_total?: number | string;
  /** Parte da taxa de serviço vinda de Mesas/Balcão e de Delivery. */
  service_tax_tables?: number | string;
  service_tax_delivery?: number | string;
}

/** As 24 horas zeradas, base para preencher o que a API não devolver. */
const emptyHours = (): HourTotal[] =>
  Array.from({ length: 24 }, (_, hour) => ({
    hour,
    label: `${String(hour).padStart(2, '0')}h`,
    total: 0,
    orders: 0,
  }));

export function useSalesAnalytics({
  storeId,
  startDate,
  endDate,
}: {
  storeId?: number | string;
  startDate: string;
  endDate: string;
}) {
  const enabled = !!storeId && isRangeReady(startDate, endDate);
  const query = buildDashboardQuery({ storeId, startDate, endDate });

  /**
   * Um 4xx aqui é contrato errado (filtro inválido, sem permissão): repetir só
   * atrasa o erro aparecer na tela.
   */
  const retry = (count: number, err: any) => {
    const status = err?.status;
    if (status >= 400 && status < 500) return false;
    return count < 2;
  };

  const fetcher = (path: string) => async () => {
    const { data } = await api.get(`${path}?${query}`);
    return data;
  };

  const sellersQuery = useQuery({
    queryKey: ['dashboard-sales-by-seller', query],
    queryFn: fetcher(Globals.api.salesBySeller),
    enabled,
    keepPreviousData: true,
    retry,
  } as any);

  const hoursQuery = useQuery({
    queryKey: ['dashboard-sales-by-hour', query],
    queryFn: fetcher(Globals.api.salesByHour),
    enabled,
    keepPreviousData: true,
    retry,
  } as any);

  const paymentsQuery = useQuery({
    queryKey: ['dashboard-sales-by-payment', query],
    queryFn: fetcher(Globals.api.salesByPayment),
    enabled,
    keepPreviousData: true,
    retry,
  } as any);

  const summaryQuery = useQuery({
    queryKey: ['dashboard-sales-summary', query],
    queryFn: fetcher(Globals.api.salesSummary),
    enabled,
    keepPreviousData: true,
    retry,
  } as any);

  const sellers = useMemo<SellerTotal[]>(() => {
    const rows: SellerRow[] = (sellersQuery.data as any)?.sellers ?? [];

    return rows
      .map((row) => ({
        id: row.seller_id ?? 'sem-vendedor',
        name: row.seller_name || 'Não informado',
        total: toNumber(row.total_value),
        orders: toNumber(row.total_orders),
      }))
      .sort((a, b) => b.total - a.total);
  }, [sellersQuery.data]);

  const hours = useMemo<HourTotal[]>(() => {
    const rows: HourRow[] = (hoursQuery.data as any)?.data ?? [];
    const buckets = emptyHours();

    rows.forEach((row) => {
      const bucket = buckets[Number(row.hour)];
      if (!bucket) return;
      bucket.total = toNumber(row.total_value);
      bucket.orders = toNumber(row.total_orders);
    });

    return buckets;
  }, [hoursQuery.data]);

  const payments = useMemo<PaymentTotal[]>(() => {
    const rows: PaymentRow[] = (paymentsQuery.data as any)?.data ?? [];

    return rows
      .map((row) => ({
        id: row.payment_type_id ?? row.payment_method ?? 'sem-forma',
        name: row.payment_method || 'Não informado',
        total: toNumber(row.total_value),
        orders: toNumber(row.total_orders),
      }))
      .sort((a, b) => b.total - a.total);
  }, [paymentsQuery.data]);

  const summary = useMemo(() => {
    const data = summaryQuery.data as SummaryResponse | undefined;

    return {
      totalValue: toNumber(data?.total_value),
      totalOrders: toNumber(data?.total_orders),
      averageTicket: toNumber(data?.average_ticket),
      customersCount: toNumber(data?.customers_served),
      newCustomers: toNumber(data?.new_customers),
      serviceTaxTotal: toNumber(data?.service_tax_total),
      // null = API ainda sem a divisão por origem; o card esconde as linhas
      // em vez de mostrar R$ 0,00, que pareceria um valor real.
      serviceTaxTables:
        data?.service_tax_tables == null ? null : toNumber(data.service_tax_tables),
      serviceTaxDelivery:
        data?.service_tax_delivery == null ? null : toNumber(data.service_tax_delivery),
    };
  }, [summaryQuery.data]);

  const queries = [sellersQuery, hoursQuery, paymentsQuery, summaryQuery];

  /**
   * A mensagem de erro é devolvida para os painéis porque, sem ela, uma falha
   * da API aparece na tela como "nenhuma venda no período" — indistinguível de
   * um período realmente vazio.
   */
  const firstError = queries.find((item) => item.isError)?.error as
    | { message?: string }
    | undefined;

  return {
    ...summary,
    sellers,
    hours,
    payments,
    isLoading: enabled && queries.some((item) => item.isLoading),
    isFetching: queries.some((item) => item.isFetching),
    isError: queries.some((item) => item.isError),
    errorMessage: firstError?.message ?? null,
    enabled,
  };
}
